import React from "react";
import { Document, Image, Page, Path, StyleSheet, Svg, Text, View } from "@react-pdf/renderer";
import {
  commonStyles,
  formatDate,
  formatPhoneNumber,
  isValidSummary,
  parseHtmlToText,
  renderBulletPoints,
} from "../Services/ReactPDFService.jsx";

// Photo Classic Template - Clean design with profile photo and classic layout
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    backgroundColor: "#ffffff",
    paddingTop: 25,
    paddingBottom: 30,
    paddingHorizontal: 40,
  },
  // Header Section with photo
  headerContainer: {
    flexDirection: "row",
    marginBottom: 20,
    // paddingBottom: 15,
    // borderBottomWidth: 1,
    borderBottomColor: "#e5e5e5",
  },
  profilePhotoContainer: {
    width: 90,
    height: 90,
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: "#f0f0f0",
    marginRight: 20,
  },
  profilePhoto: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  profilePlaceholder: {
    width: 90,
    height: 90,
    borderRadius: 6,
    backgroundColor: "#e0e0e0",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 20,
  },
  profileInitials: {
    fontSize: 28,
    fontWeight: 700,
    color: "#666666",
  },
  headerRight: {
    flex: 1,
    justifyContent: "center",
  },
  name: {
    fontSize: 28,
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: 20,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  professionalTitle: {
    fontSize: 18,
    color: "#666666",
    marginBottom: 12,
  },
  contactRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 15,
  },
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  contactIcon: {
    width: 10,
    height: 10,
    marginRight: 5,
  },
  contactText: {
    fontSize: 10,
    color: "#444444",
  },
  // Section styling
  sectionContainer: {
    marginBottom: 5,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: 10,
    textTransform: "uppercase",
    letterSpacing: 1,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: "#1a1a1a",
  },
  // Summary/About Me
  summaryText: {
    fontSize: 10,
    lineHeight: 1.7,
    color: "#333333",
    textAlign: "justify",
  },
  // Education items - standard layout like ModernSlate
  educationItem: {
    marginBottom: 12,
  },
  educationHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 2,
  },
  degree: {
    fontSize: 11,
    fontWeight: 700,
    color: "#1a1a1a",
  },
  school: {
    fontSize: 10,
    color: "#666666",
    fontStyle: "italic",
    marginTop: 1,
  },
  educationDate: {
    fontSize: 10,
    color: "#666666",
  },
  educationDescription: {
    fontSize: 10,
    lineHeight: 1.5,
    color: "#444444",
    marginTop: 4,
  },
  // Experience items - two column layout like ModernSlate
  experienceItem: {
    flexDirection: "row",
    marginBottom: 5,
  },
  experienceLeft: {
    width: "20%",
    paddingRight: 10,
  },
  experienceRight: {
    width: "80%",
  },
  companyName: {
    fontSize: 11,
    fontWeight: 700,
    color: "#1a1a1a",
  },
  jobTitle: {
    fontSize: 10,
    color: "#666666",
    fontStyle: "italic",
    marginTop: 2,
  },
  experienceDate: {
    fontSize: 10,
    color: "#666666",
    marginTop: 2,
  },
  experienceLocation: {
    fontSize: 9,
    color: "#666666",
  },
  bulletPoint: {
    fontSize: 9,
    marginBottom: 2,
    paddingLeft: 10,
    lineHeight: 1.5,
    color: "#444444",
  },
  // Skills - multi-column with bullets
  skillsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  skillColumn: {
    width: "25%",
    paddingRight: 10,
  },
  skillItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 4,
  },
  skillBullet: {
    fontSize: 9,
    color: "#1a1a1a",
    marginRight: 5,
  },
  skillText: {
    fontSize: 9,
    color: "#444444",
  },
  // References
  referencesContainer: {
    flexDirection: "row",
    gap: 30,
  },
  referenceItem: {
    flex: 1,
  },
  referenceName: {
    fontSize: 10,
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: 2,
  },
  referenceTitle: {
    fontSize: 9,
    color: "#666666",
    marginBottom: 4,
  },
  referenceContact: {
    flexDirection: "row",
    marginBottom: 2,
  },
  referenceLabel: {
    fontSize: 8,
    fontWeight: 700,
    color: "#1a1a1a",
    marginRight: 5,
  },
  referenceValue: {
    fontSize: 8,
    color: "#444444",
  },
  // Projects
  projectItem: {
    marginBottom: 10,
  },
  projectTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: 2,
  },
  projectTech: {
    fontSize: 9,
    color: "#666666",
    fontStyle: "italic",
    marginBottom: 3,
  },
  // Certifications
  certificationItem: {
    marginBottom: 8,
  },
  certName: {
    fontSize: 10,
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: 1,
  },
  certAuthority: {
    fontSize: 9,
    color: "#666666",
  },
  // Languages
  languageItem: {
    flexDirection: "row",
    marginBottom: 4,
  },
  languageName: {
    fontSize: 9,
    color: "#444444",
    fontWeight: 500,
    marginRight: 6,
  },
  languageLevel: {
    fontSize: 9,
    color: "#666666",
  },
  // Achievements
  achievementItem: {
    marginBottom: 6,
  },
  achievementText: {
    fontSize: 9,
    lineHeight: 1.5,
    color: "#444444",
  },
});

// Contact icon paths
const contactIcons = {
  phone: "M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z",
  email: "M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z",
  website: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z",
  location: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
};

const PhotoClassicTemplate = ({ resumeData, customStyles = {}, customSectionOrder = null }) => {
  const {
    personalInfo = {},
    workExperience = [],
    education = [],
    skills = {},
    projects = [],
    certifications = [],
    achievements = [],
    languages = [],
    awards = [],
    volunteering = [],
    interests = [],
    industryExpertise = [],
    publications = [],
    references = [],
    customSections = [],
    sectionTitles = {},
    summary = "",
    professionalTitle = "",
    profilePhoto = {},
  } = resumeData || {};

  // Get profile photo URL
  const profilePhotoUrl = profilePhoto?.url || personalInfo?.profilePhotoUrl || null;

  // Get professional title
  const headerTitle = professionalTitle || (workExperience.length > 0 ? workExperience[0].jobTitle : "");

  // Create dynamic styles based on customizations
  const createDynamicStyles = (baseStyles, customizations) => {
    const hasCustomizations = customizations && Object.keys(customizations).length > 0;
    if (!hasCustomizations) return baseStyles;

    const { fontSize, sectionSpacing, titleColor, subtitleColor, fontFamily } = customizations;
    const customStyleOverrides = {};

    if (fontFamily) {
      customStyleOverrides.page = { ...baseStyles.page, fontFamily };
    }

    if (fontSize) {
      customStyleOverrides.name = { ...baseStyles.name, fontSize: fontSize * 2 };
      customStyleOverrides.sectionTitle = { ...baseStyles.sectionTitle, fontSize: fontSize * 0.93 };
      customStyleOverrides.companyName = { ...baseStyles.companyName, fontSize: fontSize * 0.8 };
      customStyleOverrides.degree = { ...baseStyles.degree, fontSize: fontSize * 0.8 };
      customStyleOverrides.summaryText = { ...baseStyles.summaryText, fontSize: fontSize * 0.72 };
      customStyleOverrides.bulletPoint = { ...baseStyles.bulletPoint, fontSize: fontSize * 0.65 };
    }

    if (sectionSpacing) {
      customStyleOverrides.sectionContainer = { ...baseStyles.sectionContainer, marginBottom: sectionSpacing };
    }

    if (titleColor) {
      customStyleOverrides.name = {
        ...(customStyleOverrides.name || baseStyles.name),
        color: titleColor,
      };
      customStyleOverrides.sectionTitle = {
        ...(customStyleOverrides.sectionTitle || baseStyles.sectionTitle),
        color: titleColor,
        borderBottomColor: titleColor,
      };
      customStyleOverrides.companyName = {
        ...(customStyleOverrides.companyName || baseStyles.companyName),
        color: titleColor,
      };
      customStyleOverrides.degree = {
        ...(customStyleOverrides.degree || baseStyles.degree),
        color: titleColor,
      };
    }

    if (subtitleColor) {
      customStyleOverrides.professionalTitle = { ...baseStyles.professionalTitle, color: subtitleColor };
      customStyleOverrides.experienceDate = { ...baseStyles.experienceDate, color: subtitleColor };
      customStyleOverrides.educationDate = { ...baseStyles.educationDate, color: subtitleColor };
    }

    return { ...baseStyles, ...customStyleOverrides };
  };

  const mergedStyles = createDynamicStyles(styles, customStyles);

  // Combine all skills
  const allSkills = (() => {
    if (Array.isArray(skills)) return skills;
    if (skills.list && Array.isArray(skills.list) && skills.list.length > 0) return skills.list;
    const combinedSkills = [
      ...(skills.technical || []),
      ...(skills.soft || []),
      ...(skills.tools || []),
      ...(skills.other || []),
    ];
    if (combinedSkills.length > 0) return combinedSkills;
    if (skills.categorized) {
      return [
        ...(skills.categorized.technical || []),
        ...(skills.categorized.soft || []),
        ...(skills.categorized.tools || []),
        ...(skills.categorized.other || []),
      ];
    }
    return [];
  })();

  const getSkillName = (skill) => {
    if (typeof skill === "string") return skill;
    if (skill && typeof skill === "object") return skill.name || skill.skill || "";
    return "";
  };

  // Helper to get language name from string or object
  const getLanguageName = (lang) => {
    if (typeof lang === "string") return lang;
    if (lang && typeof lang === "object") return lang.name || lang.language || "";
    return "";
  };

  // Helper to get interest name from string or object
  const getInterestName = (interest) => {
    if (typeof interest === "string") return interest;
    if (interest && typeof interest === "object") return interest.name || interest.title || "";
    return "";
  };

  // Split skills into columns (4 columns)
  const getSkillColumns = () => {
    const columns = [[], [], [], []];
    allSkills.forEach((skill, index) => {
      columns[index % 4].push(skill);
    });
    return columns;
  };

  // Render contact icon
  const renderContactIcon = (iconPath) => (
    <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
      <Path d={iconPath} fill="#444444" />
    </Svg>
  );

  // Default section titles
  const defaultTitles = {
    summary: 'Profile', workExperience: 'Experience', education: 'Education', skills: 'Skills',
    projects: 'Projects', certifications: 'Certifications', achievements: 'Achievements',
    languages: 'Languages', awards: 'Awards', volunteering: 'Volunteering', interests: 'Interests',
    industryExpertise: 'Industry Expertise', publications: 'Publications', references: 'References',
  };
  const getSectionTitle = (key) => sectionTitles[key] || defaultTitles[key];

  // Default section order
  const defaultSectionOrder = ["summary", "education", "workExperience", "skills", "projects", "certifications", "languages", "achievements", "awards", "volunteering", "interests", "industryExpertise", "publications", "references"];

  // Build final section order - include custom sections
  const customSectionIds = customSections.filter(cs => cs.id && cs.title && cs.items?.length > 0).map(cs => cs.id);
  const baseSectionOrder = customSectionOrder || defaultSectionOrder;
  const finalSectionOrder = [...baseSectionOrder, ...customSectionIds.filter(id => !baseSectionOrder.includes(id))];

  // Section components
  const sectionComponents = {
    summary: () =>
      summary &&
      isValidSummary(summary) && (
        <View style={mergedStyles.sectionContainer}>
          <Text style={mergedStyles.sectionTitle}>Profile</Text>
          <Text style={mergedStyles.summaryText}>{parseHtmlToText(summary)}</Text>
        </View>
      ),

    education: () =>
      education.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <Text style={mergedStyles.sectionTitle}>Education</Text>
          {education.map((edu, index) => (
            <View key={index} style={mergedStyles.educationItem}>
              <View style={mergedStyles.educationHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={mergedStyles.degree}>
                    {edu.degree}
                    {edu.fieldOfStudy ? `, ${edu.fieldOfStudy}` : ""}
                  </Text>
                  <Text style={mergedStyles.school}>{edu.school}</Text>
                </View>
                <View style={{ alignItems: "flex-end" }}>
                  <Text style={mergedStyles.educationDate}>
                    {formatDate(edu.startDate)} – {edu.endDate === "Present" ? "Present" : formatDate(edu.endDate)}
                  </Text>
                  {edu.location && <Text style={mergedStyles.educationDate}>{edu.location}</Text>}
                </View>
              </View>
              {edu.description && (
                <Text style={mergedStyles.educationDescription}>{parseHtmlToText(edu.description)}</Text>
              )}
            </View>
          ))}
        </View>
      ),

    workExperience: () =>
      workExperience.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <Text style={mergedStyles.sectionTitle}>Experience</Text>
          {workExperience.map((job, index) => (
            <View key={index} style={mergedStyles.experienceItem}>
              <View style={mergedStyles.experienceLeft}>
                <Text style={mergedStyles.companyName}>{job.company}</Text>
                {job.jobTitle && <Text style={mergedStyles.jobTitle}>{job.jobTitle}</Text>}
                <Text style={mergedStyles.experienceDate}>
                  {formatDate(job.startDate)} –{" "}
                  {job.isCurrentJob || job.endDate === "Present" || job.endDate === "Current" || !job.endDate
                    ? "Present"
                    : formatDate(job.endDate)}
                </Text>
                {job.location && <Text style={mergedStyles.experienceLocation}>{job.location}</Text>}
              </View>
              <View style={mergedStyles.experienceRight}>
                {job.description && renderBulletPoints(parseHtmlToText(job.description), mergedStyles)}
              </View>
            </View>
          ))}
        </View>
      ),

    skills: () =>
      allSkills.length > 0 && (
        <View style={mergedStyles.sectionContainer} wrap={false}>
          <Text style={mergedStyles.sectionTitle}>Skills</Text>
          <View style={mergedStyles.skillsContainer}>
            {getSkillColumns().map((column, colIndex) => (
              <View key={colIndex} style={mergedStyles.skillColumn}>
                {column.map((skill, skillIndex) => (
                  <View key={skillIndex} style={mergedStyles.skillItem}>
                    <Text style={mergedStyles.skillBullet}>•</Text>
                    <Text style={mergedStyles.skillText}>{getSkillName(skill)}</Text>
                  </View>
                ))}
              </View>
            ))}
          </View>
        </View>
      ),

    projects: () =>
      projects.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <Text style={mergedStyles.sectionTitle}>Projects</Text>
          {projects.map((project, index) => (
            <View key={index} style={mergedStyles.projectItem}>
              <Text style={mergedStyles.projectTitle}>{project.name}</Text>
              {project.technologies && (
                <Text style={mergedStyles.projectTech}>Technologies: {project.technologies}</Text>
              )}
              {project.description && (
                <View style={{ marginTop: 3 }}>
                  {renderBulletPoints(parseHtmlToText(project.description), mergedStyles)}
                </View>
              )}
            </View>
          ))}
        </View>
      ),

    certifications: () =>
      certifications.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <Text style={mergedStyles.sectionTitle}>Certifications</Text>
          {certifications.map((cert, index) => (
            <View key={index} style={mergedStyles.certificationItem}>
              <Text style={mergedStyles.certName}>{cert.name}</Text>
              <Text style={mergedStyles.certAuthority}>
                {cert.authority || cert.issuer}
                {cert.date && ` • ${formatDate(cert.date)}`}
              </Text>
            </View>
          ))}
        </View>
      ),

    languages: () =>
      languages &&
      languages.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <Text style={mergedStyles.sectionTitle}>Languages</Text>
          {languages.map((lang, index) => (
            <View key={index} style={mergedStyles.languageItem}>
              <Text style={mergedStyles.languageName}>{getLanguageName(lang)}</Text>
              {lang.proficiency && <Text style={mergedStyles.languageLevel}>– {lang.proficiency}</Text>}
            </View>
          ))}
        </View>
      ),

    achievements: () =>
      achievements.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <Text style={mergedStyles.sectionTitle}>Achievements</Text>
          {achievements.map((achievement, index) => (
            <View key={index} style={mergedStyles.achievementItem}>
              {achievement.description && (
                <Text style={mergedStyles.achievementText}>• {parseHtmlToText(achievement.description)}</Text>
              )}
            </View>
          ))}
        </View>
      ),

    references: () =>
      references &&
      references.length > 0 && (
        <View style={mergedStyles.sectionContainer} wrap={false}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('references')}</Text>
          <View style={mergedStyles.referencesContainer}>
            {references.slice(0, 2).map((ref, index) => (
              <View key={index} style={mergedStyles.referenceItem}>
                <Text style={mergedStyles.referenceName}>{ref.name}</Text>
                <Text style={mergedStyles.referenceTitle}>{ref.title || ref.position}</Text>
                {ref.phone && (
                  <View style={mergedStyles.referenceContact}>
                    <Text style={mergedStyles.referenceLabel}>Phone</Text>
                    <Text style={mergedStyles.referenceValue}>{ref.phone}</Text>
                  </View>
                )}
                {ref.email && (
                  <View style={mergedStyles.referenceContact}>
                    <Text style={mergedStyles.referenceLabel}>Email</Text>
                    <Text style={mergedStyles.referenceValue}>{ref.email}</Text>
                  </View>
                )}
              </View>
            ))}
          </View>
        </View>
      ),

    awards: () => awards.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <Text style={mergedStyles.sectionTitle}>{getSectionTitle('awards')}</Text>
        {awards.map((award, index) => (
          <View key={index} style={mergedStyles.achievementItem}>
            <Text style={mergedStyles.achievementText}>• {award.title || award.name}{award.issuer && ` - ${award.issuer}`}{award.date && ` (${formatDate(award.date)})`}</Text>
          </View>
        ))}
      </View>
    ),

    volunteering: () => volunteering.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <Text style={mergedStyles.sectionTitle}>{getSectionTitle('volunteering')}</Text>
        {volunteering.map((vol, index) => (
          <View key={index} style={mergedStyles.experienceItem}>
            <View style={mergedStyles.experienceHeader}>
              <Text style={mergedStyles.companyName}>{vol.role}</Text>
              {(vol.startDate || vol.endDate) && <Text style={mergedStyles.dateRange}>{formatDate(vol.startDate)} – {vol.isCurrentRole ? "Present" : formatDate(vol.endDate)}</Text>}
            </View>
            <Text style={mergedStyles.jobTitle}>{vol.organization}</Text>
            {vol.description && <View style={{ marginTop: 4 }}>{renderBulletPoints(parseHtmlToText(vol.description), mergedStyles)}</View>}
          </View>
        ))}
      </View>
    ),

    interests: () => interests.length > 0 && (
      <View style={mergedStyles.sectionContainer} wrap={false}>
        <Text style={mergedStyles.sectionTitle}>{getSectionTitle('interests')}</Text>
        <View style={mergedStyles.skillsContainer}>
          {interests.map((interest, index) => (
            <View key={index} style={mergedStyles.skillBadge}>
              <Text style={mergedStyles.skillText}>{getInterestName(interest)}</Text>
            </View>
          ))}
        </View>
      </View>
    ),

    industryExpertise: () => industryExpertise.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <Text style={mergedStyles.sectionTitle}>{getSectionTitle('industryExpertise')}</Text>
        {industryExpertise.map((exp, index) => (
          <View key={index} style={mergedStyles.achievementItem}>
            <Text style={mergedStyles.achievementText}>• {exp.name}{exp.proficiency && ` - ${exp.proficiency}`}{exp.yearsOfExperience && ` (${exp.yearsOfExperience} years)`}</Text>
          </View>
        ))}
      </View>
    ),

    publications: () => publications.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <Text style={mergedStyles.sectionTitle}>{getSectionTitle('publications')}</Text>
        {publications.map((pub, index) => (
          <View key={index} style={mergedStyles.achievementItem}>
            <Text style={mergedStyles.achievementText}>• {pub.title}{pub.publisher && ` - ${pub.publisher}`}{pub.date && ` (${formatDate(pub.date)})`}</Text>
          </View>
        ))}
      </View>
    ),

    // Dynamic custom sections
    ...customSections.reduce((acc, customSection) => {
      if (customSection.id && customSection.title && customSection.items?.length > 0) {
        acc[customSection.id] = () => (
          <View style={mergedStyles.sectionContainer}>
            <Text style={mergedStyles.sectionTitle}>{customSection.title}</Text>
            {customSection.items.map((item, itemIndex) => (
              <View key={itemIndex} style={mergedStyles.achievementItem}>
                {item.content && <Text style={mergedStyles.achievementText}>• {parseHtmlToText(item.content)}</Text>}
              </View>
            ))}
          </View>
        );
      }
      return acc;
    }, {}),
  };

  return (
    <Document>
      <Page size="A4" style={mergedStyles.page} wrap={true}>
        {/* Header Section */}
        <View style={mergedStyles.headerContainer}>
          {profilePhotoUrl && (
            <View style={mergedStyles.profilePhotoContainer}>
              <Image src={profilePhotoUrl} style={mergedStyles.profilePhoto} />
            </View>
          )}
          <View style={mergedStyles.headerRight}>
            <Text style={mergedStyles.name}>
              {personalInfo.firstName} {personalInfo.lastName}
            </Text>
            {headerTitle && <Text style={mergedStyles.professionalTitle}>{headerTitle}</Text>}
            <View style={mergedStyles.contactRow}>
              {personalInfo.phone && (
                <View style={mergedStyles.contactItem}>
                  {renderContactIcon(contactIcons.phone)}
                  <Text style={mergedStyles.contactText}>{formatPhoneNumber(personalInfo.phone)}</Text>
                </View>
              )}
              {personalInfo.email && (
                <View style={mergedStyles.contactItem}>
                  {renderContactIcon(contactIcons.email)}
                  <Text style={mergedStyles.contactText}>{personalInfo.email}</Text>
                </View>
              )}
              {personalInfo.website && (
                <View style={mergedStyles.contactItem}>
                  {renderContactIcon(contactIcons.website)}
                  <Text style={mergedStyles.contactText}>{personalInfo.website}</Text>
                </View>
              )}
              {personalInfo.location && (
                <View style={mergedStyles.contactItem}>
                  {renderContactIcon(contactIcons.location)}
                  <Text style={mergedStyles.contactText}>{personalInfo.location}</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Main Content */}
        {finalSectionOrder.map((sectionId, index) => {
          const SectionComponent = sectionComponents[sectionId];
          const content = SectionComponent ? SectionComponent() : null;
          return content ? <View key={`${sectionId}-${index}`} wrap={true}>{content}</View> : null;
        })}
      </Page>
    </Document>
  );
};

export default PhotoClassicTemplate;
